import { functions } from "../firebase.ts";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";

// bankkey
type Status = {
    text: string;
    color: "green" | "red" | "orange";
    content?: string;
    signature?: string;
    isVerified?: boolean;
    iv?: string;
    encryptedSymmetricKey?: string;
    encryptedContent?: string;
    decryptedContent?: string;
};

export class Crypto {
    private algorithm = {
        name: "RSA-OAEP",
        hash: "SHA-256"
    };

    constructor() {}

    async add(name: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/add" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const bid: any = (
                await verify({ assertion, action: "bankkey/add", name })
            ).data;

            return {
                text: bid,
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async encrypt(bid: string, content: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/encrypt" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const {
                iv,
                encryptedSymmetricKey,
                encryptedContent,
                signature,
                message
            }: any = (
                await verify({
                    assertion,
                    action: "bankkey/encrypt",
                    bid,
                    content
                })
            ).data;

            return {
                text: message,
                color: "green",
                iv,
                encryptedSymmetricKey,
                encryptedContent,
                signature
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async decrypt(
        bid: string,
        signature: string,
        content: string, // encrypted
        iv: string
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/decrypt" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { message, decryptedContent }: any = (
                await verify({
                    assertion,
                    action: "bankkey/decrypt",
                    bid,
                    signature,
                    content,
                    iv
                })
            ).data;

            return {
                text: message,
                color: "green",
                decryptedContent
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async sign(bid: string, content: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/sign" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { message, signature }: any = (
                await verify({
                    assertion,
                    action: "bankkey/sign",
                    bid,
                    content
                })
            ).data;

            return {
                text: message,
                color: "green",
                signature
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // via client
    async verify(
        bid: string,
        data: string,
        signature: string
    ): Promise<boolean> {
        const publicKeyBuffer = this.base64urlToArrayBuffer(bid);
        const cryptoKey = await window.crypto.subtle.importKey(
            "spki", // 'SubjectPublicKeyInfo' - the standard format for public keys
            publicKeyBuffer,
            this.algorithm,
            true, // The key is extractable (though we don't need it to be)
            ["verify"] // We intend to use this key for verification
        );

        // 3. Prepare the message and signature for verification
        const encoder = new TextEncoder();
        const messageBuffer = encoder.encode(data);

        // The signature is base64, so we need to decode it into a buffer
        const signatureBuffer = Uint8Array.from(atob(signature), (c) =>
            c.charCodeAt(0)
        );

        // 4. Perform the verification
        return await window.crypto.subtle.verify(
            this.algorithm,
            cryptoKey,
            signatureBuffer,
            messageBuffer
        );
    }

    // via server: not used
    async verify2(
        bid: string,
        content: string,
        signature: string
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/verify" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { message, isVerified }: any = (
                await verify({
                    assertion,
                    action: "bankkey/verify",
                    bid,
                    content,
                    signature
                })
            ).data;

            return {
                text: message,
                color: "green",
                isVerified
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // not used bc done on client side
    async mint(
        bid: string,
        content: string, // to encrypt symmetric key
        iv: string
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bankkey/mint" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { encryptedSymmetricKey }: any = (
                await verify({
                    assertion,
                    action: "bankkey/mint",
                    bid,
                    content,
                    iv
                })
            ).data;

            return {
                text: "Success",
                color: "green",
                encryptedSymmetricKey
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // helpers

    private base64urlToArrayBuffer(base64url: string): ArrayBuffer {
        const base64 = base64url
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(base64url.length / 4) * 4, "=");

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }
}
